/**
 * WorkSection — primary location, position, employment type, earliest start.
 *
 * `locations` may be an array of either strings or `{name, lat, lng,
 * address, phone}` objects (depending on backend version).  After the
 * applicant picks a store, an inline Leaflet map animates in with a marker
 * at the store's coordinates (when available).
 */
import React, { useMemo, useState } from 'react';
import {
  Storefront, Briefcase, Clock, Calendar, MapPin, MagnifyingGlass, CheckCircle,
} from '@phosphor-icons/react';
import { Field, TextInput, Toggle, inputBase, inputStyle } from './primitives';
import { LocationMap } from './LocationMap';

function normalize(loc) {
  if (!loc) return null;
  if (typeof loc === 'string') return { name: loc };
  return loc;
}

export function WorkSection({ form, setEvt, set, locations, isLight }) {
  const [search, setSearch] = useState('');
  const normalized = useMemo(
    () => (locations || []).map(normalize).filter(Boolean),
    [locations],
  );
  const filtered = useMemo(() => {
    if (!search) return normalized;
    const q = search.toLowerCase();
    return normalized.filter(l => l.name.toLowerCase().includes(q));
  }, [normalized, search]);
  const selected = useMemo(
    () => normalized.find(l => l.name === form.work_location) || null,
    [normalized, form.work_location],
  );

  return (
    <div className="space-y-4" data-testid="section-work">
      <Field label="Primary work location" required icon={<Storefront size={11} weight="fill" />} testId="field-location"
        hint={normalized.length === 0 ? 'No locations configured yet.' : `${normalized.length} location${normalized.length === 1 ? '' : 's'} available.`}>
        <div className="relative">
          <MagnifyingGlass size={13} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2"
            style={{ color: 'var(--cs-text-muted)' }} />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search locations…"
            className={inputBase + ' pl-9 focus:border-[#10B981]'}
            style={inputStyle(isLight)}
            data-testid="input-location-search"
          />
        </div>
        <div className="max-h-56 overflow-y-auto rounded-xl mt-2 p-1.5"
          style={{ background: isLight ? 'rgba(241,245,249,0.7)' : 'rgba(0,0,0,0.3)',
                   border: `1px solid ${isLight ? '#E2E8F0' : 'rgba(255,255,255,0.06)'}` }}>
          {filtered.length === 0 && (
            <div className="text-[11px] py-3 text-center" style={{ color: 'var(--cs-text-muted)' }}>
              No matches.
            </div>
          )}
          {filtered.map(loc => {
            const active = form.work_location === loc.name;
            return (
              <button
                key={loc.name} type="button"
                onClick={() => set('work_location')(loc.name)}
                className="w-full text-left px-3 py-2 rounded-lg flex items-center gap-2 transition-all"
                style={{
                  background: active ? 'linear-gradient(135deg, #10B981, #06B6D4)' : 'transparent',
                  color: active ? '#0B0B0F' : (isLight ? '#0f172a' : '#F5F5F5'),
                }}
                data-testid={`location-option-${loc.name.replace(/\s+/g, '-')}`}>
                <MapPin size={12} weight={active ? 'fill' : 'regular'} />
                <span className="text-[12px] font-bold flex-1">{loc.name}</span>
                {active && <CheckCircle size={13} weight="fill" />}
              </button>
            );
          })}
        </div>
      </Field>

      {selected && <LocationMap location={selected} />}

      <Field label="Position interested in" icon={<Briefcase size={11} weight="fill" />} testId="field-position">
        <Toggle value={form.position} onChange={set('position')} isLight={isLight}
          testId="toggle-position"
          options={[
            { value: 'sales_consultant', label: 'Sales' },
            { value: 'lead', label: 'Lead' },
            { value: 'manager', label: 'Manager' },
            { value: 'other', label: 'Other' },
          ]} />
      </Field>

      <Field label="Employment type" icon={<Clock size={11} weight="fill" />} testId="field-emp-type">
        <Toggle value={form.employment_type} onChange={set('employment_type')} isLight={isLight}
          testId="toggle-emptype"
          options={[
            { value: 'full_time', label: 'Full-time' },
            { value: 'part_time', label: 'Part-time' },
            { value: 'either', label: 'Either' },
          ]} />
      </Field>

      <Field label="Earliest start date" icon={<Calendar size={11} weight="fill" />} testId="field-start-date">
        <TextInput type="date" value={form.earliest_start_date} onChange={setEvt('earliest_start_date')} isLight={isLight} testId="input-start-date" />
      </Field>
    </div>
  );
}
